import { ReactKeycloakProvider } from '@react-keycloak/web';
import { ComponentProps, FC, ReactNode, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import isoWeek from 'dayjs/plugin/isoWeek';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';

import keycloakConfig, { initOptions } from '@/app/keycloak';
import { AuthInitFailedContext } from '@/app/ui/AuthBootstrap';
import { UserTypeContextProvider } from '@/shared/lib/UserTypeContext/UserTypeContext';

dayjs.extend(utc);
dayjs.extend(minMax);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(isoWeek);

dayjs.updateLocale('en', {
  weekStart: 1,
});

interface Props {
  children: ReactNode | ReactNode[];
}

type KeycloakEventHandler = NonNullable<ComponentProps<typeof ReactKeycloakProvider>['onEvent']>;

const Root: FC<Props> = ({ children }) => {
  const [authInitFailed, setAuthInitFailed] = useState(false);

  const handleKeycloakEvent = useCallback<KeycloakEventHandler>((event, error) => {
    if (event === 'onInitError') {
      console.error('Keycloak init failed', error);
      setAuthInitFailed(true);
    } else if (event === 'onAuthRefreshError') {
      // The next API call gets a 401 and the query client redirects to login.
      console.error('Keycloak token refresh failed', error);
    }
  }, []);

  return (
    <ReactKeycloakProvider
      authClient={keycloakConfig}
      initOptions={initOptions}
      onEvent={handleKeycloakEvent}
    >
      <UserTypeContextProvider>
        <AuthInitFailedContext.Provider value={authInitFailed}>
          {children}
        </AuthInitFailedContext.Provider>
      </UserTypeContextProvider>
    </ReactKeycloakProvider>
  );
};

export default Root;
